import { useEffect, useState, type FormEvent } from "react";
import { categories } from "@/mocks/events";

interface EventFiltersProps {
  cat: string;
  sort: string;
  keyword: string;
  total: number;
  onCatChange: (value: string) => void;
  onSortChange: (value: string) => void;
  onKeywordChange: (value: string) => void;
}

const sortOptions = [
  { value: "latest", label: "最新更新", icon: "ri-time-line" },
  { value: "hot", label: "热度最高", icon: "ri-fire-line" },
  { value: "nodes", label: "节点最多", icon: "ri-list-check-2" },
];

export default function EventFilters({
  cat,
  sort,
  keyword,
  total,
  onCatChange,
  onSortChange,
  onKeywordChange,
}: EventFiltersProps) {
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const submitSearch = (event: FormEvent) => {
    event.preventDefault();
    onKeywordChange(input.trim());
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => onCatChange("all")}
            className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap border ${
              cat === "all"
                ? "bg-foreground-950 text-background-50 border-foreground-950"
                : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300"
            }`}
          >
            全部
          </button>
          {categories.map((item) => (
            <button
              key={item.slug}
              type="button"
              onClick={() => onCatChange(item.slug)}
              className={`px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap border ${
                cat === item.slug
                  ? "bg-primary-500 text-background-50 border-primary-500"
                  : "bg-background-50 text-foreground-700 border-background-300 hover:border-primary-300"
              }`}
            >
              {item.name}
            </button>
          ))}
        </div>

        <form onSubmit={submitSearch} className="flex items-center gap-2 w-full lg:w-auto">
          <span className="w-9 h-9 flex items-center justify-center text-foreground-400 shrink-0">
            <i className="ri-search-line" />
          </span>
          <input
            value={input}
            onChange={(event) => setInput(event.target.value)}
            placeholder="搜索事件关键词"
            className="flex-1 lg:w-56 h-10 px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400"
          />
          <button
            type="submit"
            className="h-10 px-4 rounded-md bg-background-100 border border-background-300 text-sm text-foreground-700 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
          >
            搜索
          </button>
        </form>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-4 border-t border-background-200">
        <p className="text-sm text-foreground-500">
          共找到 <span className="font-medium text-foreground-900">{total}</span> 条事件时间线
          {keyword ? (
            <span className="ml-2 text-foreground-400">关键词「{keyword}」</span>
          ) : null}
        </p>
        <div className="inline-flex items-center px-1 py-1 rounded-full bg-background-100 border border-background-200 self-start">
          {sortOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onSortChange(option.value)}
              className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full text-sm transition-colors cursor-pointer whitespace-nowrap ${
                sort === option.value
                  ? "bg-background-50 text-foreground-950 font-medium"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              <i className={option.icon} />
              {option.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}